import { IconSortAsc } from '@components/icons/sort-asc';
import { IconSortDesc } from '@components/icons/sort-desc';
import { Menu } from '@components/menu';
import { useScopedTranslate } from '@locale/translate';

type NoInfer<T> = [T][T extends unknown ? 0 : never];

export type SortMenuProps<T extends string> = {
  options: readonly T[];
  value: NoInfer<T>;
  onChange(value: NoInfer<T>): void;
  getOptionLabel(option: NoInfer<T>): string;
  /** Defaults to the option label's text before " • " (e.g. "Created • Newest" → "Created"). */
  getButtonLabel?(value: NoInfer<T>): string;
  label?: string;
  dataTestid?: string;
};

export const SortMenu = <T extends string>({
  options,
  value,
  onChange,
  getOptionLabel,
  getButtonLabel = (option) => getOptionLabel(option).split(' • ')[0],
  label,
  dataTestid,
}: SortMenuProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const isDescending = value.startsWith('-');

  return (
    <Menu
      maxWidth="auto"
      width="auto"
      buttonProps={{
        variant: 'secondary',
        text: getButtonLabel(value),
        iconLeft: isDescending ? <IconSortDesc /> : <IconSortAsc />,
      }}
      dataTestid={dataTestid}
    >
      <Menu.Label label={label ?? t('sort')} />
      {options.map((option) => (
        <Menu.Item
          key={option}
          onSelect={() => onChange(option)}
          isActive={value === option}
          label={getOptionLabel(option)}
        />
      ))}
    </Menu>
  );
};

const locale = {
  en: { sort: 'Sort by' },
  is: { sort: 'Raða eftir' },
};
